import { useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Button, Card, Grid, TextField, Typography } from "@mui/material";
import { useFormik } from "formik";

import { hideLoader, showLoader } from "@APP/redux";
import { formatErrorMessage } from "@APP/utils";
import { updateOrganization } from "@APP/services/api";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { OrganizationDetail, OrganizationFormValues } from "@APP/types";
import { organizationDetailsValidationSchema } from "@APP/utils/validations";

interface OrganizationDetailsProps {
  organizationData: OrganizationDetail;
  onUpdate: () => void;
}

const OrganizationDetails = ({ organizationData, onUpdate }: OrganizationDetailsProps) => {
  const alert = useAlert();
  const dispatch = useDispatch();
  const handleErrorCodes = useHandleErrorCodes();
  const [isEditing, setIsEditing] = useState(false);

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    resetForm,
    isSubmitting,
  } = useFormik<OrganizationFormValues>({
    initialValues: {
      name: organizationData?.name || "",
      companyName: organizationData?.businessContact?.name || "",
      companyEmail: organizationData?.businessContact?.email || "",
      companyTelephone: organizationData?.businessContact?.telephone || "",
      primaryContactName: organizationData?.primaryContact?.name || "",
      primaryContactEmail: organizationData?.primaryContact?.email || "",
      primaryContactTelephone: organizationData?.primaryContact?.telephone || "",
      // phone: organizationData?.companyInfo?.telephone || "",
    },
    validationSchema: organizationDetailsValidationSchema,
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      try {
        dispatch(showLoader());

        // Build payload with only non-empty values
        const updateData: any = {};

        // Add orgName if it has a value
        if (formValues.name?.trim()) {
          updateData.orgName = formValues.name;
        }

        // Build businessContact object with non-empty values
        const businessContact: any = {};
        if (formValues.companyName?.trim()) {
          businessContact.name = formValues.companyName;
        }
        if (formValues.companyEmail?.trim()) {
          businessContact.email = formValues.companyEmail;
        }
        if (formValues.companyTelephone?.trim()) {
          businessContact.phoneNumber = formValues.companyTelephone;
        }
        // Only add businessContact if it has at least one field
        if (Object.keys(businessContact).length > 0) {
          updateData.businessContact = businessContact;
        }

        // Build contact object with non-empty values
        const contact: any = {};
        if (formValues.primaryContactName?.trim()) {
          contact.name = formValues.primaryContactName;
        }
        if (formValues.primaryContactEmail?.trim()) {
          contact.email = formValues.primaryContactEmail;
        }
        if (formValues.primaryContactTelephone?.trim()) {
          contact.phoneNumber = formValues.primaryContactTelephone;
        }
        // Only add contact if it has at least one field
        if (Object.keys(contact).length > 0) {
          updateData.contact = contact;
        }

        await updateOrganization(organizationData.id, updateData);

        alert.open("Success", "Organisation details updated successfully.", [
          {
            text: "Okay",
          },
        ]);

        setIsEditing(false);
        onUpdate();
      } catch (error: any) {
        const errorData = error?.response?.data;

        // If no error code exists, show generic message
        if (!errorData?.errorCode) {
          alert.open(
            "Error",
            "Unable to update organisation details. Please try again or contact support if the issue persists.",
            [{ text: "Okay" }],
          );
        } else {
          // If error code exists, try to handle it
          const isHandled = handleErrorCodes(errorData.errorCode);

          if (!isHandled) {
            const errorMessage = formatErrorMessage(
              error,
              "Unable to update organisation details. Please try again or contact support if the issue persists.",
            );
            alert.open("Error", errorMessage, [{ text: "Okay" }]);
          }
        }
      } finally {
        dispatch(hideLoader());
      }
    },
  });

  const handleCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  return (
    <Card elevation={12}>
      <Box p={3}>
        <Typography variant="h5" gutterBottom>
          Edit Organisation
        </Typography>

        <form onSubmit={handleSubmit}>
          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Organisation Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Organisation Name"
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Primary Contact Name"
                  name="primaryContactName"
                  value={values.primaryContactName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.primaryContactName && errors.primaryContactName)}
                  helperText={touched.primaryContactName && errors.primaryContactName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Primary Contact Email"
                  name="primaryContactEmail"
                  value={values.primaryContactEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  type="email"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.primaryContactEmail && errors.primaryContactEmail)}
                  helperText={touched.primaryContactEmail && errors.primaryContactEmail}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Primary Contact Phone Number"
                  name="primaryContactTelephone"
                  value={values.primaryContactTelephone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.primaryContactTelephone && errors.primaryContactTelephone)}
                  helperText={touched.primaryContactTelephone && errors.primaryContactTelephone}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3}>
            <Typography variant="h6" gutterBottom>
              Organisation Contact Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contact Name"
                  name="companyName"
                  value={values.companyName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.companyName && errors.companyName)}
                  helperText={touched.companyName && errors.companyName}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contact Email"
                  name="companyEmail"
                  value={values.companyEmail}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  type="email"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.companyEmail && errors.companyEmail)}
                  helperText={touched.companyEmail && errors.companyEmail}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Contact Phone Number"
                  name="companyTelephone"
                  value={values.companyTelephone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  variant="outlined"
                  disabled={!isEditing || isSubmitting}
                  error={Boolean(touched.companyTelephone && errors.companyTelephone)}
                  helperText={touched.companyTelephone && errors.companyTelephone}
                />
              </Grid>
            </Grid>
          </Box>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
            {!isEditing ? (
              <Button variant="contained" color="primary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
            ) : (
              <>
                <Button variant="outlined" color="secondary" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={isSubmitting || Object.keys(errors).length > 0}>
                  Save
                </Button>
              </>
            )}
          </Box>
        </form>
      </Box>
    </Card>
  );
};

export default OrganizationDetails;
